import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { LEGAL } from "../lib/legal";

const PLAN_OPTIONS = [
    { key: "standard", name: "Standard", price: "€19,90/mese", limit: "5 analisi/mese", featured: true },
    { key: "pro", name: "Pro", price: "€49,90/mese", limit: "25 analisi/mese" },
];

const inputCls =
    "w-full h-12 px-3 rounded-none border border-white/15 bg-white text-sm focus:border-[#22D3EE] focus:ring-0 focus:outline-none transition-colors";
const labelCls = "text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2";

const EMPTY = {
    email: "", password: "", first_name: "", last_name: "", phone: "",
    company_name: "", vat_number: "", fiscal_code: "", address: "",
    zip_code: "", city: "", province: "", country: "Italia", sdi_code: "", pec: "",
};

function FieldText({ label, k, form, update, testid, required = true, placeholder = "", type = "text", autoComplete }) {
    return (
        <div>
            <label className={labelCls}>{label}</label>
            <input
                data-testid={testid}
                type={type}
                required={required}
                value={form[k]}
                onChange={update(k)}
                className={inputCls}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
        </div>
    );
}

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialPlan = ["standard", "pro"].includes(searchParams.get("plan"))
        ? searchParams.get("plan") : "standard";
    const [plan, setPlan] = useState(initialPlan);
    const [accountType, setAccountType] = useState("individual");
    const [form, setForm] = useState(EMPTY);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const selectedPlan = PLAN_OPTIONS.find((p) => p.key === plan);
    const isCompany = accountType === "company";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!acceptPrivacy || !acceptTerms) {
            setErr("Devi accettare Privacy Policy e Termini e Condizioni per continuare.");
            return;
        }
        if (isCompany && !form.sdi_code.trim() && !form.pec.trim()) {
            setErr("Per l'azienda serve il Codice SDI oppure la PEC (almeno uno dei due).");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                account_type: accountType,
                email: form.email,
                password: form.password,
                first_name: form.first_name,
                last_name: form.last_name,
                phone: form.phone,
                full_name: `${form.first_name} ${form.last_name}`.trim(),
                plan,
                accept_privacy: true,
                accept_terms: true,
                privacy_version: LEGAL.privacyVersion,
                terms_version: LEGAL.termsVersion,
            };
            if (isCompany) {
                Object.assign(payload, {
                    company_name: form.company_name,
                    vat_number: form.vat_number,
                    fiscal_code: form.fiscal_code || null,
                    address: form.address,
                    zip_code: form.zip_code,
                    city: form.city,
                    province: form.province,
                    country: form.country || "Italia",
                    sdi_code: form.sdi_code || null,
                    pec: form.pec || null,
                });
            }
            await register(payload);
            if (window.fbq) window.fbq("track", "CompleteRegistration");
            try {
                const { data } = await api.post("/payments/checkout", {
                    plan,
                    origin_url: window.location.origin,
                    source: "registration",
                });
                if (data?.checkout_url) {
                    window.location.href = data.checkout_url;
                    return;
                }
            } catch {
                // fallback: dashboard mostra il blocco con CTA di pagamento
            }
            navigate("/dashboard", { replace: true });
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-[2fr_3fr] bg-white" data-testid="register-page">
            {/* ── Pannello form (40%) ─────────────────────────────── */}
            <div className="flex flex-col p-8 md:p-14 lg:p-16 min-h-screen">
                <div className="flex items-baseline justify-between border-b border-white/10 pb-4 mb-10">
                    <Link to="/" className="flex items-center">
                        <img src="/logo-full.png" alt="Asta Intelligence" className="h-24 w-auto" />
                    </Link>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-tight">
                        Nuovo operatore
                    </div>
                </div>

                <div className="w-full max-w-sm fade-up">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#22D3EE] mb-3">
                        Pagamento sicuro con Stripe
                    </div>
                    <h1 className="font-display-black text-4xl uppercase leading-[0.92] mb-3">
                        Crea il tuo
                        <br />
                        account
                    </h1>
                    <p className="text-sm text-slate-400 mb-6">
                        Dopo la registrazione completi il pagamento su Stripe e il piano si attiva subito.
                    </p>

                    {/* Toggle Privato / Azienda */}
                    <div className="grid grid-cols-2 gap-2 mb-6" data-testid="register-account-type">
                        {[
                            { key: "individual", label: "Privato", sub: "Investitore personale" },
                            { key: "company", label: "Azienda", sub: "Con dati di fatturazione" },
                        ].map((t) => (
                            <button
                                type="button"
                                key={t.key}
                                data-testid={`register-type-${t.key}`}
                                onClick={() => setAccountType(t.key)}
                                className={`p-3 rounded-none border text-left transition-colors ${accountType === t.key ? "border-[#22D3EE] bg-[#22D3EE]/10 ring-1 ring-[#22D3EE]" : "border-white/15 hover:border-[#22D3EE]/50"}`}
                            >
                                <div className="text-sm font-bold uppercase">{t.label}</div>
                                <div className="text-[10px] text-slate-400">{t.sub}</div>
                            </button>
                        ))}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                        <div>
                            <label className={labelCls}>Piano operativo</label>
                            <div className="grid grid-cols-2 gap-2" data-testid="register-plan-selector">
                                {PLAN_OPTIONS.map((p) => (
                                    <button
                                        type="button"
                                        key={p.key}
                                        data-testid={`register-plan-${p.key}`}
                                        onClick={() => setPlan(p.key)}
                                        className={`p-2.5 rounded-none border text-left transition-colors relative ${plan === p.key ? "border-[#22D3EE] bg-[#22D3EE]/10 ring-1 ring-[#22D3EE]" : "border-white/15 hover:border-[#22D3EE]/50"}`}
                                    >
                                        {p.featured && (
                                            <span className="absolute -top-2 right-1 bg-[#7C3AED] text-white text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 font-bold">
                                                Consigliato
                                            </span>
                                        )}
                                        <div className="text-xs font-bold uppercase">{p.name}</div>
                                        <div className="text-[11px] text-slate-400">{p.price}</div>
                                        <div className="text-[10px] text-[#22D3EE] font-semibold">{p.limit}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {isCompany && (
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-t border-white/10 pt-4">
                                Referente
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <FieldText label="Nome" k="first_name" form={form} update={update}
                                       testid="register-firstname-input" placeholder="Mario" autoComplete="given-name" />
                            <FieldText label="Cognome" k="last_name" form={form} update={update}
                                       testid="register-lastname-input" placeholder="Rossi" autoComplete="family-name" />
                        </div>
                        <FieldText label="Email" k="email" form={form} update={update} type="email"
                                   testid="register-email-input" placeholder="mario@esempio.it" autoComplete="email" />
                        <FieldText label="Cellulare" k="phone" form={form} update={update} type="tel"
                                   testid="register-phone-input" placeholder="+39 333 1234567" autoComplete="tel" />
                        <div>
                            <label className={labelCls}>Password — min 8 caratteri, una lettera e un numero</label>
                            <input
                                data-testid="register-password-input"
                                type="password"
                                required
                                minLength={8}
                                value={form.password}
                                onChange={update("password")}
                                className={inputCls}
                                placeholder="••••••••"
                                autoComplete="new-password"
                            />
                        </div>

                        {isCompany && (
                            <div className="space-y-5" data-testid="register-company-section">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-t border-white/10 pt-4">
                                    Dati azienda / fatturazione
                                </div>
                                <FieldText label="Ragione sociale" k="company_name" form={form} update={update}
                                           testid="register-company-name" placeholder="Rossi Immobiliare S.r.l." autoComplete="organization" />
                                <div className="grid grid-cols-2 gap-3">
                                    <FieldText label="Partita IVA" k="vat_number" form={form} update={update}
                                               testid="register-vat" placeholder="01234567890" />
                                    <FieldText label="Codice fiscale (se diverso)" k="fiscal_code" form={form} update={update}
                                               testid="register-fiscal-code" required={false} placeholder="Opzionale" />
                                </div>
                                <FieldText label="Indirizzo sede legale" k="address" form={form} update={update}
                                           testid="register-address" placeholder="Via Roma 1" autoComplete="street-address" />
                                <div className="grid grid-cols-3 gap-3">
                                    <FieldText label="CAP" k="zip_code" form={form} update={update}
                                               testid="register-zip" placeholder="50123" autoComplete="postal-code" />
                                    <FieldText label="Comune" k="city" form={form} update={update}
                                               testid="register-city" placeholder="Firenze" />
                                    <FieldText label="Provincia" k="province" form={form} update={update}
                                               testid="register-province" placeholder="FI" />
                                </div>
                                <FieldText label="Nazione" k="country" form={form} update={update}
                                           testid="register-country" placeholder="Italia" autoComplete="country-name" />
                                <div className="grid grid-cols-2 gap-3">
                                    <FieldText label="Codice SDI" k="sdi_code" form={form} update={update}
                                               testid="register-sdi" required={false} placeholder="es. M5UXCR1" />
                                    <FieldText label="PEC" k="pec" form={form} update={update} type="email"
                                               testid="register-pec" required={false} placeholder="azienda@pec.it" />
                                </div>
                                <p className="text-[10px] text-slate-400 -mt-3">
                                    Serve il Codice SDI <span className="font-bold">oppure</span> la PEC per la fatturazione elettronica.
                                </p>
                            </div>
                        )}

                        <div className="space-y-2 border-t border-white/10 pt-4">
                            <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer">
                                <input
                                    type="checkbox"
                                    data-testid="register-accept-privacy"
                                    checked={acceptPrivacy}
                                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                                    className="mt-0.5 accent-black w-4 h-4 shrink-0"
                                />
                                <span>
                                    Ho letto e accetto la{" "}
                                    <Link to="/privacy" target="_blank" className="font-semibold text-black underline decoration-1 underline-offset-2 hover:text-[#22D3EE]">Privacy Policy</Link>.
                                </span>
                            </label>
                            <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer">
                                <input
                                    type="checkbox"
                                    data-testid="register-accept-terms"
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.target.checked)}
                                    className="mt-0.5 accent-black w-4 h-4 shrink-0"
                                />
                                <span>
                                    Accetto i{" "}
                                    <Link to="/termini" target="_blank" className="font-semibold text-black underline decoration-1 underline-offset-2 hover:text-[#22D3EE]">Termini e Condizioni</Link>{" "}
                                    e dichiaro di aver compreso il{" "}
                                    <Link to="/disclaimer-ai" target="_blank" className="font-semibold text-black underline decoration-1 underline-offset-2 hover:text-[#22D3EE]">Disclaimer AI</Link>.
                                </span>
                            </label>
                        </div>

                        {err && (
                            <div
                                data-testid="register-error"
                                className="rounded-none border border-red-300 bg-red-50 text-red-700 text-xs px-3 py-2.5"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            data-testid="register-submit-button"
                            type="submit"
                            disabled={loading || !acceptPrivacy || !acceptTerms}
                            className="w-full h-12 rounded-none bg-black text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#7C3AED] disabled:opacity-40 transition-colors"
                        >
                            {loading ? "Attivazione in corso…" : "Crea account"}
                        </button>
                    </form>

                    <div className="mt-8 border-t border-white/10 pt-4 pb-8 text-xs text-slate-400">
                        Hai già un account?{" "}
                        <Link
                            data-testid="goto-login-link"
                            to="/login"
                            className="font-semibold text-black underline decoration-1 underline-offset-4 hover:text-[#22D3EE]"
                        >
                            Accedi
                        </Link>
                    </div>
                </div>
            </div>

            {/* ── Pannello immagine (60%) ─────────────────────────── */}
            <div className="fd hidden md:block relative bg-[#04060B] overflow-hidden">
                <img
                    src="/italia-notte.jpg"
                    alt="L'Italia fotografata di notte dalla Stazione Spaziale Internazionale"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

                <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-8 text-white/60">
                    <span className="text-[10px] uppercase tracking-[0.2em]">Registro operatori</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-mono-tight">Aste giudiziarie · Italia</span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 text-white">
                    <div className="font-display-black uppercase text-3xl lg:text-[2.6rem] leading-[0.95]">
                        Ogni tribunale.
                        <br />
                        Ogni perizia.
                        <br />
                        <span className="text-[#22D3EE]">Un solo report.</span>
                    </div>
                    <div className="mt-6 flex items-end justify-between gap-4 border-t border-white/15 pt-4">
                        <p className="text-[11px] text-white/60 font-mono-tight" data-testid="register-plan-recap">
                            Piano selezionato: <span className="text-[#22D3EE] uppercase font-bold">{selectedPlan.name}</span>
                            {" — "}{selectedPlan.limit} · {selectedPlan.price}
                        </p>
                        <p className="text-[10px] text-white/30 font-mono-tight whitespace-nowrap shrink-0">
                            42°N 12°E · Foto: NASA / ISS
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
